"use client";

import { X } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../../command";
import { GlassBadge, Input, type FieldVariant } from "components/ui/v2";
import type { CircleActionVariant } from "components/ui/v2/circle-action-variants";

interface TagInputProps {
  readonly placeholder?: string;
  readonly prefix?: string;
  readonly title?: string;
  readonly tags: string[] | undefined;
  readonly setTags: (tags: string[] | undefined) => void;
  readonly maxTags?: number;
  readonly suggestions?: string[];
  readonly variant?: FieldVariant;
  readonly inputSize?: "default" | "sm";
  readonly label?: string;
}

function fieldVariantToBadgeVariant(variant: FieldVariant): CircleActionVariant {
  const map: Record<FieldVariant, CircleActionVariant> = {
    violet: "violet",
    sky: "sky",
    amber: "gold",
    emerald: "emerald",
    rose: "rose",
    slate: "slate",
    subtle: "slate",
  };
  return map[variant] ?? "violet";
}

export default function TagInput(props: TagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [placeholderMessage, setPlaceholderMessage] = useState<string>("");

  const { t } = useTranslation("explore");
  const fieldVariant = props.variant ?? "slate";
  const badgeVariant = fieldVariantToBadgeVariant(fieldVariant);

  const filteredSuggestions = useMemo(() => {
    const prefix = props.prefix ?? "";
    return (
      props.suggestions
        ?.filter((suggestion) =>
          suggestion
            .toLowerCase()
            .includes(inputValue.slice(prefix.length).toLowerCase())
        )
        .filter((suggestion) => !props.tags?.includes(suggestion)) || []
    );
  }, [inputValue, props.suggestions, props.prefix, props.tags]);

  const remainingTags = useMemo(() => {
    return props.maxTags ? props.maxTags - (props.tags?.length ?? 0) : Infinity;
  }, [props.maxTags, props.tags]);

  const handleInputChange = (value: string) => {
    if (remainingTags === 0) return;
    const prefix = props.prefix ?? "";
    setInputValue(value.startsWith(prefix) ? value : prefix);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim().length > 0) {
      e.preventDefault();
      const prefix = props.prefix ?? "";
      addTag(inputValue.slice(prefix.length).trim());
    }
  };

  const addTag = (newTag: string) => {
    if (newTag && !props.tags?.includes(newTag) && remainingTags > 0) {
      props.setTags([...(props.tags ?? []), newTag]);
      setInputValue(props.prefix ?? "");
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    props.setTags((props.tags ?? []).filter((tag) => tag !== tagToRemove));
  };

  const fieldLabel =
    props.label ?? props.placeholder ?? t("Agregar tags");

  useEffect(() => {
    if (remainingTags === 0) {
      setPlaceholderMessage("");
      setShowSuggestions(false);
    } else {
      setPlaceholderMessage(
        t("Recuerda, solo puedes agregar numero tags", {
          numero: remainingTags,
        }) ?? ""
      );
      setShowSuggestions(true);
    }

    if ((props.tags ?? []).length === 0) {
      props.setTags(undefined);
    }
  }, [props.tags, remainingTags, t, props.setTags]);

  return (
    <div className="w-full space-y-2">
      {props.title && (
        <label className="mb-2 block text-sm font-semibold">{props.title}</label>
      )}

      <div className="relative">
        <Command className="overflow-visible bg-transparent">
          <Input
            ref={inputRef}
            label={fieldLabel}
            size={props.inputSize ?? "sm"}
            variant={fieldVariant}
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleInputKeyDown}
            disabled={remainingTags === 0}
            helperText={remainingTags > 0 && remainingTags !== Infinity ? placeholderMessage : undefined}
          />

          {showSuggestions &&
            inputValue.length > 0 &&
            filteredSuggestions.length > 0 && (
              <CommandList className="absolute left-0 top-full z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover shadow-md">
                <CommandEmpty>No results found.</CommandEmpty>
                <CommandGroup>
                  {filteredSuggestions.map((suggestion) => (
                    <CommandItem
                      key={suggestion}
                      onSelect={() => addTag(suggestion)}
                      className="cursor-pointer"
                    >
                      {suggestion}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            )}
        </Command>
      </div>

      {(props.tags ?? []).length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
          {(props.tags ?? []).map((tag) => (
            <GlassBadge
              key={tag}
              variant={badgeVariant}
              as="div"
              size="sm"
              active
            >
              <span className="max-w-[10rem] truncate">
                {props.prefix}
                {tag}
              </span>
              <button
                type="button"
                onClick={() => removeTag(tag)}
                aria-label={t("Eliminar")}
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-black/10 transition-colors hover:bg-black/20 dark:bg-white/15 dark:hover:bg-white/25"
              >
                <X className="h-2.5 w-2.5" strokeWidth={2.5} />
              </button>
            </GlassBadge>
          ))}
        </div>
      )}
    </div>
  );
}
